/**
 * A patched ResizeObserver that wraps callbacks in requestAnimationFrame to prevent
 * "ResizeObserver loop completed with undelivered notifications" errors.
 *
 * This error occurs when ResizeObserver callbacks modify the DOM during the observation cycle,
 * triggering additional resize events that can't be delivered in the same frame. By deferring
 * callback execution to the next animation frame, we ensure the observation cycle completes
 * before any DOM modifications occur.
 */
export class PatchedResizeObserver implements ResizeObserver {
  private observer: ResizeObserver;
  private pendingFrameId: number | null = null;

  constructor(callback: ResizeObserverCallback) {
    const OriginalResizeObserver = (typeof window !== 'undefined' && window.ResizeObserver) || ResizeObserver;
    this.observer = new OriginalResizeObserver((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
      // Cancel any pending frame to coalesce multiple notifications
      if (this.pendingFrameId !== null) {
        cancelAnimationFrame(this.pendingFrameId);
      }
      this.pendingFrameId = requestAnimationFrame(() => {
        this.pendingFrameId = null;
        callback(entries, observer);
      });
    });
  }

  observe(target: Element, options?: ResizeObserverOptions): void {
    this.observer.observe(target, options);
  }

  unobserve(target: Element): void {
    this.observer.unobserve(target);
  }

  disconnect(): void {
    // Cancel pending callback to prevent execution after disconnect
    if (this.pendingFrameId !== null) {
      cancelAnimationFrame(this.pendingFrameId);
      this.pendingFrameId = null;
    }
    this.observer.disconnect();
  }
}

/**
 * The global ResizeObserver constructor, plus the marker set on it to prevent double-patching.
 */
export type PatchableResizeObserverConstructor = typeof ResizeObserver & { __isPatched?: boolean };

/**
 * Patches the global ResizeObserver to use the batched implementation.
 * Should be called once during application initialization.
 */
export function patchGlobalResizeObserver(): void {
  if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver: PatchableResizeObserverConstructor = window.ResizeObserver;

    // Only patch if not already patched
    if (!OriginalResizeObserver.__isPatched) {
      const PatchedGlobalResizeObserver: PatchableResizeObserverConstructor = class extends OriginalResizeObserver {
        private pendingFrameId: number | null = null;

        constructor(callback: ResizeObserverCallback) {
          super((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
            // Cancel any pending frame to coalesce multiple notifications
            if (this.pendingFrameId !== null) {
              cancelAnimationFrame(this.pendingFrameId);
            }
            this.pendingFrameId = requestAnimationFrame(() => {
              this.pendingFrameId = null;
              callback(entries, observer);
            });
          });
        }

        disconnect(): void {
          // Cancel pending callback to prevent execution after disconnect
          if (this.pendingFrameId !== null) {
            cancelAnimationFrame(this.pendingFrameId);
            this.pendingFrameId = null;
          }
          super.disconnect();
        }
      };

      // Mark as patched to prevent double-patching
      PatchedGlobalResizeObserver.__isPatched = true;
      window.ResizeObserver = PatchedGlobalResizeObserver;
    }
  }
}
